import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  apiGet,
  apiPost,
  fetchSchoolsSimple,
  API_BASE_URL,
} from '../services/api';
import LoadingSpinnerAdvanced from '../components/LoadingSpinnerAdvanced';
import SearchableSelect from '../components/SearchableSelect';
import RefreshButton from '../components/RefreshButton';

/**
 * Pagina de Frequencia de Funcionarios (RH).
 */

const STATUS_OPTIONS = [
  { value: 'presente', label: 'Presente', short: 'P', color: 'bg-green-500 text-white dark:bg-green-600 font-semibold' },
  { value: 'ausente', label: 'Ausente', short: 'F', color: 'bg-red-500 text-white dark:bg-red-600 font-semibold' },
  { value: 'justificado', label: 'Falta Justificada', short: 'FJ', color: 'bg-amber-500 text-white dark:bg-amber-600 font-semibold' },
  { value: 'ferias', label: 'Ferias', short: 'FE', color: 'bg-sky-500 text-white dark:bg-sky-600 font-semibold' },
  { value: 'licenca', label: 'Licenca', short: 'LI', color: 'bg-purple-500 text-white dark:bg-purple-600 font-semibold' },
  { value: 'feriado', label: 'Feriado / Recesso', short: 'FR', color: 'bg-slate-600 text-white dark:bg-slate-500 font-semibold' },
];

const STATUS_MAP = Object.fromEntries(STATUS_OPTIONS.map(s => [s.value, s]));

function toYYYYMM(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/* Combobox de escola pesquisavel */
function SchoolCombobox({ escolas, value, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef(null);

  const selected = escolas.find(s => String(s.id) === String(value));
  const label = selected ? (selected.school_name || selected.school) : '';

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = useMemo(() => {
    if (!query.trim()) return escolas;
    const q = query.toLowerCase();
    return escolas.filter(s => (s.school_name || s.school || '').toLowerCase().includes(q));
  }, [escolas, query]);

  return (
    <div ref={boxRef} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className="w-full text-left border border-slate-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-sm flex items-center justify-between shadow-sm hover:border-slate-400 dark:hover:border-slate-500 disabled:opacity-60"
      >
        <span className={!label ? 'text-slate-400 dark:text-slate-500' : 'truncate pr-2'}>
          {label || 'Selecione uma escola...'}
        </span>
        <span className="text-xs opacity-60 ml-2">&#9662;</span>
      </button>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl max-h-72 overflow-hidden flex flex-col">
          <div className="border-b border-slate-200 dark:border-slate-700 px-3 py-2 bg-slate-50 dark:bg-slate-900/60">
            <input
              type="text"
              autoFocus
              placeholder="Pesquisar escola..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="w-full text-sm bg-transparent outline-none text-slate-900 dark:text-slate-100 placeholder-slate-400"
            />
          </div>
          <div className="overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-xs text-slate-500 italic text-center">Nenhuma escola encontrada</div>
            )}
            {filtered.map(s => {
              const isSel = String(s.id) === String(value);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => { onChange(String(s.id)); setOpen(false); setQuery(''); }}
                  className={'w-full text-left px-3 py-2 text-sm transition-colors ' + (isSel ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-900 dark:text-blue-100 font-semibold' : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-100')}
                >
                  {s.school_name || s.school}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* Popover de status por celula */
function StatusPopover({ anchorRect, currentStatus, onSelect, onClose }) {
  const popRef = useRef(null);

  useEffect(() => {
    const clickHandler = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) onClose();
    };
    const escHandler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', clickHandler);
    document.addEventListener('keydown', escHandler);
    return () => {
      document.removeEventListener('mousedown', clickHandler);
      document.removeEventListener('keydown', escHandler);
    };
  }, [onClose]);

  if (!anchorRect) return null;
  const popW = 220;
  const popH = 300;
  let top = anchorRect.bottom + 6;
  let left = anchorRect.left;
  if (left + popW > window.innerWidth) left = window.innerWidth - popW - 8;
  if (top + popH > window.innerHeight) top = anchorRect.top - popH - 6;
  if (left < 8) left = 8;
  if (top < 8) top = 8;

  return (
    <div
      ref={popRef}
      className="fixed z-50 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl p-1.5"
      style={{ top, left, width: popW }}
    >
      <div className="text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400 px-2 py-1">Marcar status</div>
      {STATUS_OPTIONS.map(opt => {
        const isCur = currentStatus === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onSelect(opt.value)}
            className={'w-full text-left text-sm px-2 py-1.5 rounded flex items-center gap-2 mt-0.5 transition-colors ' + (isCur ? 'bg-blue-50 dark:bg-blue-900/40 ring-1 ring-blue-400' : 'hover:bg-slate-100 dark:hover:bg-slate-700')}
          >
            <span className={opt.color + ' w-7 h-7 rounded flex items-center justify-center text-[10px]'}>{opt.short}</span>
            <span className="text-slate-800 dark:text-slate-100 flex-1">{opt.label}</span>
          </button>
        );
      })}
      <div className="border-t border-slate-200 dark:border-slate-700 mt-1 pt-1">
        <button
          onClick={() => onSelect(null)}
          className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 flex items-center gap-2"
        >
          <span className="w-7 h-7 rounded bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-[10px] text-slate-500">-</span>
          Limpar registro
        </button>
      </div>
    </div>
  );
}

function FrequenciaFuncionarios() {
  const { user, getUserCity } = useAuth();
  const userCity = getUserCity();
  const { darkMode } = useTheme();

  const [escolas, setEscolas] = useState([]);
  const [idSchool, setIdSchool] = useState('');
  const [month, setMonth] = useState(toYYYYMM(new Date()));

  const [grid, setGrid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [dirty, setDirty] = useState({});
  const [showJustifyModal, setShowJustifyModal] = useState(false);
  const [justifyCell, setJustifyCell] = useState(null);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [holidayDay, setHolidayDay] = useState('');
  const [cellMenu, setCellMenu] = useState(null);

  const allowed = user && [ROLES.ADMIN, ROLES.COORDENADOR].includes(user.role);

  useEffect(() => {
    fetchSchoolsSimple(userCity?.id || null)
      .then(list => {
        setEscolas(list || []);
        if (list && list.length && !idSchool) setIdSchool(String(list[0].id));
      })
      .catch(() => setToast({ type: 'error', msg: 'Erro ao carregar escolas' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCity?.id]);

  const loadGrid = useCallback(async () => {
    if (!idSchool || !month) return;
    setLoading(true);
    setDirty({});
    try {
      const data = await apiGet(`/employee-attendance/monthly-grid/?id_school=${idSchool}&month=${month}`);
      setGrid(data);
    } catch (e) {
      setToast({ type: 'error', msg: 'Erro ao carregar grade' });
      setGrid(null);
    } finally {
      setLoading(false);
    }
  }, [idSchool, month]);

  useEffect(() => { loadGrid(); }, [loadGrid]);

  const setCell = (empId, day, status, justification = '') => {
    setDirty(prev => ({ ...prev, [`${empId}:${day}`]: { status, justification } }));
  };

  const getCell = (emp, day) => {
    const k = `${emp.id_employee}:${day}`;
    if (dirty[k]) return dirty[k];
    return emp.days[day] || null;
  };

  const handleCellClick = (emp, day, ev) => {
    if (!allowed) return;
    const rect = ev.currentTarget.getBoundingClientRect();
    const cur = getCell(emp, day);
    setCellMenu({
      empId: emp.id_employee,
      day,
      rect: { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right },
      currentStatus: cur?.status || null,
      currentJustification: cur?.justification || '',
    });
  };

  const handlePopoverSelect = (value) => {
    if (!cellMenu) return;
    const { empId, day, currentJustification } = cellMenu;
    if (value === 'justificado') {
      setJustifyCell({ empId, day, status: 'justificado', justification: currentJustification || '' });
      setShowJustifyModal(true);
      setCellMenu(null);
      return;
    }
    if (value === null) {
      setCell(empId, day, null, '');
    } else {
      setCell(empId, day, value, '');
    }
    setCellMenu(null);
  };

  const weekdays = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
  const getWeekdayLabel = (day) => {
    if (!month) return '';
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1, day);
    return weekdays[d.getDay()];
  };
  const isWeekend = (day) => {
    if (!month) return false;
    const [y, m] = month.split('-').map(Number);
    const dow = new Date(y, m - 1, day).getDay();
    return dow === 0 || dow === 6;
  };

  const markRowPresent = (emp) => {
    if (!grid) return;
    setDirty(prev => {
      const next = { ...prev };
      for (let d = 1; d <= grid.days_in_month; d++) {
        if (isWeekend(d)) continue;
        const cur = prev[`${emp.id_employee}:${d}`] || emp.days[d];
        if (!cur) next[`${emp.id_employee}:${d}`] = { status: 'presente', justification: '' };
      }
      return next;
    });
  };

  const clearRow = (emp) => {
    if (!grid) return;
    setDirty(prev => {
      const next = { ...prev };
      for (let d = 1; d <= grid.days_in_month; d++) {
        next[`${emp.id_employee}:${d}`] = { status: null, justification: '' };
      }
      return next;
    });
  };

  const handleSaveJustify = () => {
    if (!justifyCell) return;
    setCell(justifyCell.empId, justifyCell.day, 'justificado', justifyCell.justification || '');
    setShowJustifyModal(false);
    setJustifyCell(null);
  };

  const handleSaveAll = async () => {
    if (!idSchool) return;
    const records = Object.entries(dirty)
      .filter(([, v]) => v.status !== null)
      .map(([k, v]) => {
        const [empId, day] = k.split(':').map(Number);
        const dt = `${month}-${String(day).padStart(2, '0')}`;
        return { id_employee: empId, attendance_date: dt, status: v.status, justification: v.justification || '' };
      });
    if (records.length === 0) {
      setToast({ type: 'info', msg: 'Nada para salvar' });
      return;
    }
    setSaving(true);
    try {
      const res = await apiPost('/employee-attendance/bulk-save/', { id_school: Number(idSchool), records });
      setToast({ type: 'success', msg: `Salvo: ${res.created} criados, ${res.updated} atualizados${res.errors?.length ? `, ${res.errors.length} erros` : ''}` });
      await loadGrid();
    } catch (e) {
      setToast({ type: 'error', msg: 'Erro ao salvar' });
    } finally {
      setSaving(false);
    }
  };

  const handleMarkHoliday = async () => {
    if (!holidayDay || !idSchool) return;
    const dt = `${month}-${String(holidayDay).padStart(2, '0')}`;
    setSaving(true);
    try {
      const res = await apiPost('/employee-attendance/mark-holiday/', { id_school: Number(idSchool), attendance_date: dt, status: 'feriado' });
      setToast({ type: 'success', msg: `Feriado marcado: ${res.created} criados, ${res.updated} atualizados em ${res.total_employees} funcionarios` });
      setShowHolidayModal(false);
      setHolidayDay('');
      await loadGrid();
    } catch (e) {
      setToast({ type: 'error', msg: 'Erro ao marcar feriado' });
    } finally {
      setSaving(false);
    }
  };

  const authDownload = async (url, fallbackName) => {
    try {
      // A sessao vai no cookie httpOnly; o navegador a anexa sozinho.
      const resp = await fetch(url, { credentials: 'include' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const cd = resp.headers.get('Content-Disposition') || '';
      const m = /filename="?([^"]+)"?/.exec(cd);
      const fname = m ? m[1] : fallbackName;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) {
      setToast({ type: 'error', msg: 'Erro ao gerar download' });
    }
  };
  const downloadSchoolSheet = () => {
    if (!idSchool || !month) return;
    authDownload(`${API_BASE_URL}/employee-attendance/export-sheet/?id_school=${idSchool}&month=${month}`, `folha_presenca_${idSchool}_${month}.xlsx`);
  };
  const downloadEmployeeReport = (empId) => {
    if (!empId || !month) return;
    const qs = `id_employee=${empId}&month=${month}${idSchool ? `&id_school=${idSchool}` : ''}`;
    authDownload(`${API_BASE_URL}/employee-attendance/export-monthly-report/?${qs}`, `frequencia_${empId}_${month}.xlsx`);
  };

  const daysHeader = useMemo(() => {
    if (!grid) return [];
    return Array.from({ length: grid.days_in_month }, (_, i) => i + 1);
  }, [grid]);

  if (!allowed) {
    return (
      <div className="p-8">
        <div className="bg-red-100 text-red-800 p-4 rounded">Acesso restrito a Administrador e Coordenador.</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4 min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Frequencia de Funcionarios</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Clique em uma celula para escolher o status no menu (sem precisar clicar varias vezes).
          </p>
        </div>
        <RefreshButton onClick={loadGrid} loading={loading} />
      </div>

      <div className="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-md border border-slate-200 dark:border-slate-700">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_200px] gap-3 mb-3">
          <div>
            <label className="block text-xs font-semibold mb-1 text-slate-700 dark:text-slate-300">Escola</label>
            <SchoolCombobox escolas={escolas} value={idSchool} onChange={setIdSchool} />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 text-slate-700 dark:text-slate-300">Mes</label>
            <input
              type="month"
              className="w-full border border-slate-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-sm shadow-sm"
              value={month}
              onChange={e => setMonth(e.target.value)}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium shadow-sm disabled:opacity-50"
            onClick={handleSaveAll}
            disabled={saving || Object.keys(dirty).length === 0}
          >
            {saving ? 'Salvando...' : `Salvar ${Object.keys(dirty).length || ''}`}
          </button>
          <button
            className="bg-slate-200 hover:bg-slate-300 text-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-100 px-4 py-2 rounded-md text-sm font-medium border border-slate-300 dark:border-slate-600"
            onClick={() => setShowHolidayModal(true)}
            disabled={!idSchool}
          >
            Marcar Feriado
          </button>
          <button
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium shadow-sm disabled:opacity-50"
            onClick={downloadSchoolSheet}
            disabled={!idSchool}
          >
            Exportar Folha (Excel)
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        {STATUS_OPTIONS.map(s => (
          <span key={s.value} className={`${s.color} px-2.5 py-1 rounded shadow-sm`}>
            {s.short} = {s.label}
          </span>
        ))}
      </div>

      {toast && (
        <div
          className={`p-2 rounded text-sm cursor-pointer ${
            toast.type === 'success'
              ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
              : toast.type === 'error'
              ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'
          }`}
          onClick={() => setToast(null)}
          role="button"
        >
          {toast.msg} <span className="ml-2 text-xs opacity-60">(clique para fechar)</span>
        </div>
      )}

      {loading && <LoadingSpinnerAdvanced />}
      {!loading && grid && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md overflow-auto border border-slate-200 dark:border-slate-700">
          <table className="text-xs border-collapse min-w-full">
            <thead>
              <tr className="bg-slate-200 dark:bg-slate-900 text-slate-800 dark:text-slate-100 sticky top-0">
                <th className="p-2 text-left border-r border-slate-300 dark:border-slate-700 sticky left-0 bg-slate-200 dark:bg-slate-900 z-10 font-semibold">Funcionario</th>
                {daysHeader.map(d => (
                  <th
                    key={d}
                    className={`p-1 border-r border-slate-300 dark:border-slate-700 text-center font-semibold ${isWeekend(d) ? 'bg-slate-300 dark:bg-slate-800 text-slate-700 dark:text-slate-300' : ''}`}
                    style={{ minWidth: 32 }}
                  >
                    <div className="text-sm">{d}</div>
                    <div className="text-[10px] font-normal text-slate-600 dark:text-slate-400">{getWeekdayLabel(d)}</div>
                  </th>
                ))}
                <th className="p-2 text-center font-semibold">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {grid.employees.length === 0 && (
                <tr>
                  <td colSpan={daysHeader.length + 2} className="p-6 text-center text-slate-500 dark:text-slate-400 italic">
                    Nenhum funcionario vinculado a esta escola.
                  </td>
                </tr>
              )}
              {grid.employees.map((emp, idx) => {
                const rowBg = idx % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-800/60';
                const stickyBg = idx % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-slate-50 dark:bg-slate-800/95';
                return (
                  <tr key={emp.id_employee} className={'border-t border-slate-200 dark:border-slate-700 ' + rowBg}>
                    <td className={'p-2 border-r border-slate-200 dark:border-slate-700 whitespace-nowrap sticky left-0 ' + stickyBg}>
                      <div className="font-medium text-slate-900 dark:text-slate-100">{emp.teacher_name}</div>
                      {emp.role && (<div className="text-[10px] text-slate-500 dark:text-slate-400">{emp.role}</div>)}
                      <div className="flex gap-1 mt-1">
                        <button
                          onClick={() => markRowPresent(emp)}
                          className="text-[10px] bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 px-1.5 py-0.5 rounded hover:bg-green-200 dark:hover:bg-green-900"
                          title="Marca todos os dias uteis restantes como Presente"
                        >
                          Tudo P
                        </button>
                        <button
                          onClick={() => clearRow(emp)}
                          className="text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 px-1.5 py-0.5 rounded hover:bg-slate-200 dark:hover:bg-slate-600"
                          title="Remove todos os registros desta linha (apos Salvar)"
                        >
                          Limpar
                        </button>
                      </div>
                    </td>
                    {daysHeader.map(d => {
                      const cell = getCell(emp, d);
                      const opt = cell && cell.status && STATUS_MAP[cell.status];
                      const isDirty = !!dirty[emp.id_employee + ':' + d];
                      const title = cell && cell.status
                        ? (opt?.label || cell.status) + (cell.justification ? ' - ' + cell.justification : '')
                        : 'Sem registro (clique para marcar)';
                      let cellBg;
                      if (opt) cellBg = opt.color;
                      else if (isWeekend(d)) cellBg = 'bg-slate-100 dark:bg-slate-700/40 hover:bg-slate-200 dark:hover:bg-slate-700';
                      else cellBg = 'hover:bg-blue-50 dark:hover:bg-slate-700';
                      const ring = isDirty ? ' ring-2 ring-inset ring-blue-500' : '';
                      return (
                        <td
                          key={d}
                          onClick={(ev) => handleCellClick(emp, d, ev)}
                          className={'p-0 border-r border-slate-200 dark:border-slate-700 text-center cursor-pointer select-none transition-colors ' + cellBg + ring}
                          title={title}
                          style={{ minWidth: 32, height: 32 }}
                        >
                          {opt?.short || ''}
                        </td>
                      );
                    })}
                    <td className="p-1 text-center">
                      <button
                        onClick={() => downloadEmployeeReport(emp.id_employee)}
                        className="text-blue-600 dark:text-blue-400 hover:underline text-xs font-medium"
                      >
                        Excel
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {cellMenu && (
        <StatusPopover
          anchorRect={cellMenu.rect}
          currentStatus={cellMenu.currentStatus}
          onSelect={handlePopoverSelect}
          onClose={() => setCellMenu(null)}
        />
      )}

      {showJustifyModal && justifyCell && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-4 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold mb-2 text-slate-900 dark:text-slate-100">Falta Justificada</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
              Dia {justifyCell.day}/{month.split('-')[1]}/{month.split('-')[0]} - a justificativa eh <strong>opcional</strong>, pode deixar em branco e editar depois.
            </p>
            <textarea
              className="w-full border border-slate-300 dark:border-slate-600 rounded p-2 bg-white dark:bg-slate-700 text-sm text-slate-900 dark:text-slate-100"
              rows={3}
              placeholder="Motivo da falta (ex: atestado medico) - opcional"
              value={justifyCell.justification}
              onChange={e => setJustifyCell({ ...justifyCell, justification: e.target.value })}
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                className="px-3 py-1 rounded border border-slate-300 dark:border-slate-600 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                onClick={() => { setShowJustifyModal(false); setJustifyCell(null); }}
              >
                Cancelar
              </button>
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm"
                onClick={handleSaveJustify}
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}

      {showHolidayModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-4 w-full max-w-sm shadow-xl">
            <h3 className="text-lg font-bold mb-2 text-slate-900 dark:text-slate-100">Marcar Feriado</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              Aplica "Feriado / Recesso" para todos os funcionarios da escola no dia escolhido.
            </p>
            <label className="block text-xs font-semibold mb-1 text-slate-700 dark:text-slate-300">Dia</label>
            <SearchableSelect
              value={holidayDay}
              onChange={setHolidayDay}
              options={(grid && daysHeader ? daysHeader : []).map(d => ({
                value: d,
                label: `${String(d).padStart(2, '0')}/${month.split('-')[1]}/${month.split('-')[0]}`,
              }))}
              placeholder="Selecione um dia..."
              darkMode={darkMode}
              className="w-full mb-3"
            />
            <div className="flex justify-end gap-2">
              <button
                className="px-3 py-1 rounded border border-slate-300 dark:border-slate-600 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                onClick={() => setShowHolidayModal(false)}
              >
                Cancelar
              </button>
              <button
                className="bg-slate-700 hover:bg-slate-800 dark:bg-slate-600 dark:hover:bg-slate-500 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
                onClick={handleMarkHoliday}
                disabled={!holidayDay || saving}
              >
                {saving ? 'Aplicando...' : 'Marcar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FrequenciaFuncionarios;
